import * as React from 'react';
import { AutocompleteInput, useNotify } from 'react-admin';
import { useController } from 'react-hook-form';
import { goApi } from '../../../services';
import { apiRoutes } from '../../../services/apiRoutes';

type MasterDataItem = {
    id: string;
    name: string;
    display_order?: number;
};

type MasterDataType = 'class' | 'subject' | 'chapter' | 'topic';

interface MasterDataAutoCompleteProps {
    source?: string;
    label?: string;
    type: MasterDataType;
    filterId?: string;
    subjectId?: string;
    onChange?: (value: any) => void;
}

const DEFAULT_SOURCES = {
    class: 'masterClassId',
    subject: 'masterSubjectId',
    chapter: 'masterChapterId',
    topic: 'masterTopicId',
};

const DEFAULT_LABELS = {
    class: 'Master Class',
    subject: 'Master Subject',
    chapter: 'Master Chapter',
    topic: 'Master Topic',
};

export const MasterDataAutoComplete = (props: MasterDataAutoCompleteProps) => {
    const { type, filterId, subjectId, onChange, source, label, ...restProps } = props;
    const fieldSource = source || DEFAULT_SOURCES[type];
    const fieldLabel = label || DEFAULT_LABELS[type];

    const { field } = useController({ name: fieldSource });
    const notify = useNotify();

    const [choices, setChoices] = React.useState<MasterDataItem[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        // Chapter requires subjectId
        if (type === 'chapter' && !filterId) {
            setChoices([]);
            field.onChange(null);
            return;
        }

        // Topic requires both chapterId (filterId) and subjectId
        if (type === 'topic' && (!filterId || !subjectId)) {
            setChoices([]);
            field.onChange(null);
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                let endpoint: string;

                if (type === 'chapter') {
                    endpoint = apiRoutes.MASTER_CHAPTERS(filterId);
                } else if (type === 'topic') {
                    endpoint = apiRoutes.MASTER_TOPICS(filterId, subjectId);
                } else if (type === 'class') {
                    endpoint = apiRoutes.MASTER_CLASSES;
                } else {
                    endpoint = apiRoutes.MASTER_SUBJECTS;
                }

                const response = await goApi.get<any>(endpoint);
                const items = response?.data?.items || response?.data || [];

                // Sort by display_order if available
                const sortedItems = items.sort(
                    (a: MasterDataItem, b: MasterDataItem) =>
                        (a.display_order || 0) - (b.display_order || 0),
                );

                setChoices(sortedItems);
            } catch (error) {
                notify(`Failed to load ${fieldLabel.toLowerCase()}`, { type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [type, filterId, subjectId, notify, field, fieldLabel]);

    return (
        <AutocompleteInput
            {...restProps}
            {...field}
            source={fieldSource}
            label={fieldLabel}
            choices={choices}
            optionText="name"
            optionValue="id"
            loading={loading}
            fullWidth
            onChange={value => {
                field.onChange(value);
                if (onChange) onChange(value);
            }}
        />
    );
};
